import { ReceptorRecord } from '@trustero/trustero-api-web/lib/agent/receptor_pb'
import { showInfoToast } from 'src/Utils/helpers/toast'

/**
 * Picks the receptor row a folder scan should browse and run through.
 *
 * An account can hold several rows of the same document receptor model: one
 * per connection, re-created connections, and rows left behind when a user
 * skipped the credential step. Resolving "the Confluence receptor" by model id
 * alone returns whichever row the database hands back first, which is how a
 * folder picker ends up on a dead connection while the user has just
 * authorized another one. The choice is made explicit instead:
 *
 * 1. the row the caller asked for (the connection the user clicked, or the
 *    one an existing folder scan is bound to),
 * 2. an enabled row with valid, non-empty credentials,
 * 3. any enabled row,
 * 4. the first row of that model.
 */
export const pickFolderScanReceptor = (
  receptors: ReceptorRecord[] | undefined,
  modelId: string,
  preferredId?: string,
): ReceptorRecord | undefined => {
  const candidates = (receptors || []).filter(
    (receptor) => receptor.getModelid() === modelId,
  )
  if (candidates.length === 0) return undefined

  if (preferredId) {
    const preferred = candidates.find(
      (receptor) => receptor.getId() === preferredId,
    )
    if (preferred) return preferred
  }

  return (
    candidates.find(
      (receptor) =>
        receptor.getIsenabled() &&
        receptor.getIscredvalid() &&
        !!receptor.getCredentials(),
    ) ||
    candidates.find((receptor) => receptor.getIsenabled()) ||
    candidates[0]
  )
}

/**
 * True when an active receptor cannot work until someone re-enters or
 * re-authorizes its credentials: it is enabled but has no credentials (the
 * credential step was skipped) or its credentials were rejected.
 */
export const receptorNeedsCredentials = (
  receptor: ReceptorRecord | undefined,
): boolean =>
  !!receptor &&
  receptor.getIsenabled() &&
  (!receptor.getCredentials() || !receptor.getIscredvalid())

export const ADD_FOLDER_SCAN_ACTION = 'adding a folder scan'

/**
 * What the user was about to do with a document picker, for the credentials
 * message. The pickers serve evidence linking, policy linking and folder scans.
 */
export const credentialsActionForPickerSource = (source: string): string => {
  switch (source) {
    case 'FolderScan':
      return ADD_FOLDER_SCAN_ACTION
    case 'Policy':
      return 'linking policy documents'
    default:
      return 'linking evidence'
  }
}

/**
 * Why this receptor cannot be used for `action` right now, worded for the
 * user, or undefined when the receptor is usable. Distinguishes a receptor
 * whose credential step was skipped from one whose credentials the provider
 * has rejected, and points at the action that fixes each.
 */
export const receptorCredentialsProblem = (
  receptor: ReceptorRecord | undefined,
  receptorName: string,
  action: string = ADD_FOLDER_SCAN_ACTION,
): string | undefined => {
  if (!receptorNeedsCredentials(receptor)) return undefined
  if (!receptor?.getCredentials()) {
    return `${receptorName} has no credentials yet. Add them with "Edit credentials" on the receptor before ${action}.`
  }
  return `${receptorName} credentials are invalid or expired. Re-authorize it with "Edit credentials" on the receptor before ${action}.`
}

/**
 * Guard for actions that need a working receptor: when it cannot be used,
 * tells the user why and returns true so the caller skips opening a picker
 * that would only spin and fail.
 */
export const blockIfReceptorNeedsCredentials = (
  receptor: ReceptorRecord | undefined,
  receptorName: string,
  action: string = ADD_FOLDER_SCAN_ACTION,
): boolean => {
  const problem = receptorCredentialsProblem(receptor, receptorName, action)
  if (!problem) return false
  showInfoToast(problem)
  return true
}
